import ComponentsManager from "../ComponentsManager.js";
import LanguageManager from "../LanguageManager.js";
import ApiService from "../serverConnection/ApiService.js";
import UserProfile from "../serverConnection/UserProfile.js";
import LocalStats from "../serverConnection/LocalStats.js";
import OverlayManager from "../OverlayManager.js";
import ProfileView from "./centerMenuTab/profileView.js";
import StatView from "./centerMenuTab/statView.js";
import MatchView from "./centerMenuTab/matchView.js";

class CenterMenu {
    #fullscreen = false
    #buttonAlreadyClicked = false

    /**
     * @author Corentin (ccharton) Charton
     * @description Initialize the center menu module
     */
    constructor() {
        const mutationObserver = new MutationObserver((_, observer) => {
            if (document.getElementById('centerMenu')) {
                this.hydrate();
                observer.disconnect();
            }
        });
        mutationObserver.observe(document.body, { childList: true, subtree: true });
    
        window.addEventListener('profileUpdated', () => {
                this.hydrate();
            });
    }

    /**
     * @author Sonam (scrumier) Crumiere
     * Edited by Corentin (ccharton) Charton
     * @description Set the user profile information in the center menu that was get from the Backend
     */
    hydrate() {
        UserProfile.updateProfileIfNeeded()

        const nameEl = document.getElementById('centerMenuHeaderDisplayName');
        const displayName = UserProfile.getDisplayName()
        if (nameEl && displayName) {
            nameEl.innerText = displayName;
        }
    
        const picEl = document.getElementById('centerMenuProfilePicture');
        const profilePicture = UserProfile.getProfilePicture()
        if (picEl && profilePicture) {
            picEl.src = profilePicture
        }

    
        const dot    = document.getElementById('profileOnlineIndicator');
        const picker = document.getElementById('onlineStatusPicker');
        const loginStatus = UserProfile.getLoginStatus();
        if (dot && loginStatus) {
            dot.classList.remove('online', 'away', 'busy', 'offline');
            dot.classList.add(loginStatus);
        }
        if (picker && loginStatus) {
            picker.dataset.i18n = loginStatus;
        }
    
        LanguageManager.updateContent();
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Show or hide the user status picker
     */
    showStatusPicker() {
        const userMenuWrapper = document.getElementById('userMenuWrapper');
        if (userMenuWrapper) {
            if (userMenuWrapper.classList.contains('open')) {
                userMenuWrapper.classList.remove('open')
            } else {
                userMenuWrapper.classList.toggle('open')
            }
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the user login status and update the UI
     * @param status {string} - The status to set
     */
    setUserLoginStatus(status) {
        this.showStatusPicker()

        const dot = document.querySelector("#profileOnlineIndicator")
        if (dot) {
            dot.classList.remove("online", "away", "busy", "offline")
            dot.classList.add(status)
        }
    
        const picker = document.querySelector("#onlineStatusPicker")
        if (picker) {
            picker.dataset.i18n = status
        }
    
        LanguageManager.updateContent()

        ApiService.updateProfile({ loginStatus: status}).catch(() => {});
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the user login status to online
     */
    setOnlineStatus() {
        if (UserProfile.isAnonymous()) {
            return
        }

        this.setUserLoginStatus('online')
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the user login status to away
     */
    setAwayStatus() {
        if (UserProfile.isAnonymous()) {
            return
        }

        this.setUserLoginStatus('away')
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the user login status to busy
     */
    setBusyStatus() {
        if (UserProfile.isAnonymous()) {
            return
        }

        this.setUserLoginStatus('busy')
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the user login status to offline
     */
    setOfflineStatus() {
        if (UserProfile.isAnonymous()) {
            return
        }

        this.setUserLoginStatus('offline')
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Send disconnect request to backend and request page reload
     */
    disconnectUser() {
        this.showStatusPicker()
        ApiService.logout().catch(() => {})

        const isAnonymous = UserProfile.isAnonymous()
        UserProfile.clearProfile()
        cookieStore.getAll().then((data) => {
            data.forEach(cookie => {
                cookieStore.delete(cookie.name)
            })
        })
        if (isAnonymous) {
            OverlayManager.reset()
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the class pickerBtnActive to the new active button
     * @param newActiveBtn {HTMLElement} - The new active button
     * @returns {boolean} - True if the active button has changed, false otherwise
     */
    pickerBtnActive(newActiveBtn) {
        const pickerBtns = document.querySelectorAll('.pickerBtn')
        let oldActiveBtn = null

        pickerBtns.forEach(btn => {
            if (btn.classList.contains('pickerBtnActive')) {
                oldActiveBtn = btn
            }
        })

        if (oldActiveBtn === null || oldActiveBtn !== newActiveBtn) {
            newActiveBtn?.classList.add('pickerBtnActive')
            if (oldActiveBtn !== null) {
                oldActiveBtn.classList.remove('pickerBtnActive')
            }
            return true
        }
        return false
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Show the profile tab
     */
    showProfileView(event) {
        if (this.#buttonAlreadyClicked) {
            return
        }
        this.#buttonAlreadyClicked = true

        if (event?.target?.closest('.statusBtn')) {
            this.showStatusPicker()
        }

        const profileTabBtn = document.querySelector('#profileTabBtn')
        if (profileTabBtn && this.pickerBtnActive(profileTabBtn)) {
            ComponentsManager.loadComponent('profileTabCenterMenu', '#centerMenuTabContainer', './overlay/components/centerMenuTab/profileView.html', './overlay/styles/centerMenuTab/profileView.css').then(() =>  {
                this.#buttonAlreadyClicked = false
                ProfileView.hydrate()
            })
        } else {
            this.#buttonAlreadyClicked = false
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Show the stat tab. This is a public deployment: stats are computed
     * client-side and read from LocalStats (localStorage), so unlike the original this
     * no longer requires a real (non-guest) account.
     */
    showStatView() {
        if (this.#buttonAlreadyClicked) {
            return
        }
        this.#buttonAlreadyClicked = true

        const statTabBtn = document.querySelector('#statTabBtn')
        if (statTabBtn && this.pickerBtnActive(statTabBtn)) {
            ComponentsManager.loadComponent('statTabCenterMenu', '#centerMenuTabContainer', './overlay/components/centerMenuTab/statView.html', './overlay/styles/centerMenuTab/statView.css').then(() => {
                this.#buttonAlreadyClicked = false
                StatView.injectAllCategories(LocalStats.getStats())
            })
        } else {
            this.#buttonAlreadyClicked = false
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * Edited by: Sonam (scrumier) Crumiere
     * @description Show the match history tab. This is a public deployment: match
     * history is built client-side and read from LocalStats (localStorage), so unlike
     * the original this no longer requires a real (non-guest) account.
     */
    showMatchView() {
        if (this.#buttonAlreadyClicked) {
            return
        }
        this.#buttonAlreadyClicked = true

        const matchTabBtn = document.querySelector('#matchTabBtn')
        if (matchTabBtn && this.pickerBtnActive(matchTabBtn)) {
            ComponentsManager.loadComponent('matchTabCenterMenu', '#centerMenuTabContainer', './overlay/components/centerMenuTab/matchView.html', './overlay/styles/centerMenuTab/matchView.css').then(() => {
                this.#buttonAlreadyClicked = false
                MatchView.updateHistory()
            })
        } else {
            this.#buttonAlreadyClicked = false
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Switch between tab view in fullscreen and normal view
     */
    switchOnlyTabView() {
        if (!this.#fullscreen) {
            this.#fullscreen = true;

            const centerMenuHeader = document.querySelector('#centerMenuHeader')
            if (centerMenuHeader) {
                centerMenuHeader.classList.add('hidden')
            }

            const centerMenuTabPicker = document.querySelector('#centerMenuTabPicker')
            if (centerMenuTabPicker) {
                centerMenuTabPicker.classList.add('hidden')
            }

            const centerMenuContent = document.querySelector('#centerMenuContent')
            if (centerMenuContent) {
                if (window.innerHeight > window.innerWidth) {
                    centerMenuContent.style.height = '100%'
                } else {
                    centerMenuContent.style.height = '98%'
                }
            }

            const footer = document.querySelector('#footer')
            if (footer) {
                footer.classList.add('hidden')
            }

            const centerMenuContainer = document.querySelector('#centerMenuContainer')
            if (centerMenuContainer) {
                centerMenuContainer.style.height = '100dvh'
            }

            const fullscreenSwitchImg = document.querySelector('#fullscreenSwitchImg')
            if (fullscreenSwitchImg) {
                fullscreenSwitchImg.src = '/img/overlay/center/centerMenu/fullscreenExit.svg'
            }

        } else {
            this.#fullscreen = false;

            const centerMenuHeader = document.querySelector('#centerMenuHeader')
            if (centerMenuHeader) {
                centerMenuHeader.classList.remove('hidden')
            }

            const centerMenuTabPicker = document.querySelector('#centerMenuTabPicker')
            if (centerMenuTabPicker) {
                centerMenuTabPicker.classList.remove('hidden')
            }

            const centerMenuContent = document.querySelector('#centerMenuContent')
            if (centerMenuContent) {
                centerMenuContent.style.height = '84%'
            }

            const footer = document.querySelector('#footer')
            if (footer) {
                footer.classList.remove('hidden')
            }

            const centerMenuContainer = document.querySelector('#centerMenuContainer')
            if (centerMenuContainer) {
                centerMenuContainer.style.height = '90dvh'
            }

            const fullscreenSwitchImg = document.querySelector('#fullscreenSwitchImg')
            if (fullscreenSwitchImg) {
                fullscreenSwitchImg.src = '/img/overlay/center/centerMenu/fullscreen.svg'
            }
        }
    }

    reset() {
        const mutationObserver = new MutationObserver((_, observer) => {
            if (document.getElementById('centerMenu')) {
                this.hydrate();
                observer.disconnect();
            }
        });

        mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

}

export default new CenterMenu()